import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import {
  useGetCategoriesQuery,
  useGetEntriesQuery,
  useCreateEntryMutation,
  useUpdateEntryMutation,
  useDeleteEntryMutation,
  OLYMPIC_ORG_ID,
} from "@api/mApi";
import { isGlobalAdmin } from "@api/brokerAuth";
import type { CategoryOut, EntryOut } from "@api/types";

/**
 * Picklist Admin — RCI super-admins curate the Oxbo-M catalog picklists
 * (activity code labels, NPT categories, …) that drive the Time/NPT screen
 * labels and (via the backend) NPT classification. Reads the categories +
 * their entries from Oxbo-M and edits them in admin-mode; every mutation is
 * dual-gated server-side (a non-admin gets 403).
 *
 * Category priority: the O&G ones surface first; whatever else exists renders
 * after. The NPT-flag column binds to `properties.is_npt`, except the
 * `npt_categories` category which binds to `properties.counts_as_npt`.
 */

/** The O&G categories we prioritize at the top of the list. */
const PRIORITY_KEYS = [
  "activity_codes",
  "activity_subcodes",
  "activity_classes",
  "activity_phases",
  "npt_levels",
  "npt_categories",
];

/** Whether a 403 (admins-only) is buried in an RTK Query error. */
function isForbidden(err: unknown): boolean {
  return !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403;
}

export function PicklistAdmin() {
  const { t } = useLocale();

  // Defense-in-depth: the nav item is admin-gated and the backend 403s, but
  // also block the screen itself for a non-admin who deep-links here.
  if (!isGlobalAdmin()) {
    return (
      <div>
        <div className="page-head">
          <h1>{t("pick.title")}</h1>
        </div>
        <div className="card">
          <div className="empty-state">
            <strong>{t("pick.notAuthorized")}</strong>
            <div style={{ marginTop: 8, color: "var(--dim)" }}>{t("pick.notAuthorized.detail")}</div>
          </div>
        </div>
      </div>
    );
  }

  return <PicklistAdminInner />;
}

function PicklistAdminInner() {
  const { t } = useLocale();
  const { data: categories = [], isLoading, isError, refetch } = useGetCategoriesQuery();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Group by category_group, with the O&G categories floated to the top of a
  // synthetic "Priority" bucket order. Within a group, priority-listed keys
  // come first, then alphabetical by display name.
  const groups = useMemo(() => groupCategories(categories), [categories]);

  const selected = categories.find((c) => c.category_key === selectedKey) ?? null;

  return (
    <div>
      <div className="page-head">
        <h1>{t("pick.title")}</h1>
        <p className="subtitle">{t("pick.subtitle")}</p>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={categories.length === 0}
        emptyMessage={t("pick.noCategories")}
        onRetry={refetch}
      >
        <div className="pick-layout">
          <aside className="card pick-cats">
            <h3>{t("pick.categories")}</h3>
            {groups.map((g) => (
              <div key={g.name ?? "_"} className="pick-cat-group">
                {g.name ? <div className="pick-cat-group-name">{g.name}</div> : null}
                {g.items.map((c) => (
                  <button
                    key={c.category_key}
                    type="button"
                    className={`pick-cat-item${c.category_key === selectedKey ? " active" : ""}`}
                    onClick={() => setSelectedKey(c.category_key)}
                    title={c.category_key}
                  >
                    <span className="pick-cat-label">{c.display_name || c.category_key}</span>
                    <span className="pick-cat-key mono">{c.category_key}</span>
                  </button>
                ))}
              </div>
            ))}
          </aside>

          <section className="pick-entries">
            {selected ? (
              <CategoryEntries category={selected} />
            ) : (
              <div className="card">
                <div className="empty-state">{t("pick.selectCategory")}</div>
              </div>
            )}
          </section>
        </div>
      </QueryState>
    </div>
  );
}

interface CatGroup {
  name: string | null;
  items: CategoryOut[];
}

/** Group categories by category_group; O&G keys float to the front of each group. */
function groupCategories(categories: CategoryOut[]): CatGroup[] {
  const byGroup = new Map<string | null, CategoryOut[]>();
  for (const c of categories) {
    const g = c.category_group ?? null;
    const list = byGroup.get(g) ?? [];
    list.push(c);
    byGroup.set(g, list);
  }
  const rank = (c: CategoryOut) => {
    const i = PRIORITY_KEYS.indexOf(c.category_key);
    return i === -1 ? PRIORITY_KEYS.length : i;
  };
  const sortItems = (list: CategoryOut[]) =>
    [...list].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return (a.display_name || a.category_key).localeCompare(b.display_name || b.category_key);
    });
  // Group order: groups that contain a priority category first, then alpha.
  const groups: CatGroup[] = [...byGroup.entries()].map(([name, items]) => ({
    name,
    items: sortItems(items),
  }));
  const groupHasPriority = (g: CatGroup) => g.items.some((c) => PRIORITY_KEYS.includes(c.category_key));
  return groups.sort((a, b) => {
    const pa = groupHasPriority(a) ? 0 : 1;
    const pb = groupHasPriority(b) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

/** The NPT-flag property key for a category (special-cased for npt_categories). */
function nptFlagKey(categoryKey: string): "is_npt" | "counts_as_npt" {
  return categoryKey === "npt_categories" ? "counts_as_npt" : "is_npt";
}

/** Read a boolean property, defaulting counts_as_npt to true when absent. */
function readNptFlag(entry: EntryOut, categoryKey: string): boolean {
  const key = nptFlagKey(categoryKey);
  const v = entry.properties?.[key];
  if (typeof v === "boolean") return v;
  // counts_as_npt defaults true for npt_categories; is_npt defaults false.
  return key === "counts_as_npt";
}

function CategoryEntries({ category }: { category: CategoryOut }) {
  const { t } = useLocale();
  const categoryKey = category.category_key;
  const { data: entries = [], isLoading, isError, refetch } = useGetEntriesQuery(categoryKey);
  const showNptCategory = categoryKey === "activity_codes";

  return (
    <div className="card">
      <h3>
        {category.display_name || categoryKey} <span className="pick-cat-key mono">{categoryKey}</span>
      </h3>
      <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("pick.col.code")}</th>
                <th>{t("pick.col.label")}</th>
                <th className="num">{t("pick.col.npt")}</th>
                {showNptCategory ? <th>{t("pick.col.nptCategory")}</th> : null}
                <th className="num">{t("pick.col.active")}</th>
                <th>{t("pick.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={showNptCategory ? 6 : 5} className="pick-empty-cell">
                    {t("pick.noEntries")}
                  </td>
                </tr>
              ) : (
                entries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    categoryKey={categoryKey}
                    showNptCategory={showNptCategory}
                  />
                ))
              )}
              <AddEntryRow categoryKey={categoryKey} showNptCategory={showNptCategory} />
            </tbody>
          </table>
        </div>
      </QueryState>
    </div>
  );
}

function EntryRow({
  entry,
  categoryKey,
  showNptCategory,
}: {
  entry: EntryOut;
  categoryKey: string;
  showNptCategory: boolean;
}) {
  const { t } = useLocale();
  const [updateEntry, { isLoading, error }] = useUpdateEntryMutation();
  const [deleteEntry, { isLoading: isDeleting }] = useDeleteEntryMutation();

  const flagKey = nptFlagKey(categoryKey);
  const [label, setLabel] = useState(entry.display_label);
  const [npt, setNpt] = useState(readNptFlag(entry, categoryKey));
  const [nptCategory, setNptCategory] = useState(
    typeof entry.properties?.npt_category === "string" ? entry.properties.npt_category : "",
  );
  const [active, setActive] = useState(entry.is_active);

  const origNptCategory =
    typeof entry.properties?.npt_category === "string" ? entry.properties.npt_category : "";
  const dirty =
    label !== entry.display_label ||
    npt !== readNptFlag(entry, categoryKey) ||
    active !== entry.is_active ||
    (showNptCategory && nptCategory !== origNptCategory);

  const save = () => {
    // Merge into the existing properties bag — never drop other keys.
    const properties: Record<string, unknown> = { ...(entry.properties ?? {}) };
    properties[flagKey] = npt;
    if (showNptCategory) {
      if (nptCategory) properties.npt_category = nptCategory;
      else delete properties.npt_category;
    }
    void updateEntry({
      categoryKey,
      id: entry.id,
      body: {
        display_label: label,
        is_active: active,
        properties,
      },
    });
  };

  const remove = () => {
    if (!window.confirm(t("pick.deleteConfirm"))) return;
    void deleteEntry({ categoryKey, id: entry.id });
  };

  const forbidden = isForbidden(error);

  return (
    <tr data-owner="m">
      <td className="mono">{entry.entry_key}</td>
      <td>
        <input
          className="pick-input"
          type="text"
          value={label}
          onChange={(ev) => setLabel(ev.target.value)}
          aria-label={t("pick.col.label")}
        />
      </td>
      <td className="num">
        <input
          className="brand-check"
          type="checkbox"
          checked={npt}
          onChange={(ev) => setNpt(ev.target.checked)}
          aria-label={t("pick.col.npt")}
        />
      </td>
      {showNptCategory ? (
        <td>
          <input
            className="pick-input"
            type="text"
            value={nptCategory}
            onChange={(ev) => setNptCategory(ev.target.value)}
            aria-label={t("pick.col.nptCategory")}
          />
        </td>
      ) : null}
      <td className="num">
        <input
          className="brand-check"
          type="checkbox"
          checked={active}
          onChange={(ev) => setActive(ev.target.checked)}
          aria-label={t("pick.col.active")}
        />
      </td>
      <td>
        <div className="pick-row-actions">
          <button
            type="button"
            className="btn primary"
            disabled={!dirty || isLoading}
            onClick={save}
          >
            {isLoading ? t("pick.saving") : t("pick.save")}
          </button>
          <button type="button" className="btn" disabled={isDeleting} onClick={remove}>
            {isDeleting ? t("pick.deleting") : t("pick.delete")}
          </button>
        </div>
        {error ? (
          <div className="pick-error">{forbidden ? t("pick.forbidden") : t("pick.saveError")}</div>
        ) : null}
      </td>
    </tr>
  );
}

function AddEntryRow({
  categoryKey,
  showNptCategory,
}: {
  categoryKey: string;
  showNptCategory: boolean;
}) {
  const { t } = useLocale();
  const [createEntry, { isLoading, error }] = useCreateEntryMutation();
  const flagKey = nptFlagKey(categoryKey);

  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [npt, setNpt] = useState(flagKey === "counts_as_npt");
  const [nptCategory, setNptCategory] = useState("");
  const [active, setActive] = useState(true);

  const canAdd = code.trim() !== "" && label.trim() !== "" && !isLoading;

  const add = async () => {
    const properties: Record<string, unknown> = { [flagKey]: npt };
    if (showNptCategory && nptCategory) properties.npt_category = nptCategory;
    const res = await createEntry({
      categoryKey,
      body: {
        entry_key: code.trim(),
        display_label: label.trim(),
        scope: "org",
        admin_org_id: OLYMPIC_ORG_ID,
        is_active: active,
        properties,
      },
    })
      .unwrap()
      .catch(() => null);
    if (res) {
      setCode("");
      setLabel("");
      setNpt(flagKey === "counts_as_npt");
      setNptCategory("");
      setActive(true);
    }
  };

  const forbidden = isForbidden(error);

  return (
    <tr className="pick-add-row">
      <td>
        <input
          className="pick-input mono"
          type="text"
          value={code}
          onChange={(ev) => setCode(ev.target.value)}
          placeholder={t("pick.add.codePlaceholder")}
          aria-label={t("pick.add.codePlaceholder")}
        />
      </td>
      <td>
        <input
          className="pick-input"
          type="text"
          value={label}
          onChange={(ev) => setLabel(ev.target.value)}
          placeholder={t("pick.add.labelPlaceholder")}
          aria-label={t("pick.add.labelPlaceholder")}
        />
      </td>
      <td className="num">
        <input
          className="brand-check"
          type="checkbox"
          checked={npt}
          onChange={(ev) => setNpt(ev.target.checked)}
          aria-label={t("pick.col.npt")}
        />
      </td>
      {showNptCategory ? (
        <td>
          <input
            className="pick-input"
            type="text"
            value={nptCategory}
            onChange={(ev) => setNptCategory(ev.target.value)}
            aria-label={t("pick.col.nptCategory")}
          />
        </td>
      ) : null}
      <td className="num">
        <input
          className="brand-check"
          type="checkbox"
          checked={active}
          onChange={(ev) => setActive(ev.target.checked)}
          aria-label={t("pick.col.active")}
        />
      </td>
      <td>
        <button type="button" className="btn primary" disabled={!canAdd} onClick={add}>
          {isLoading ? t("pick.saving") : t("pick.add.button")}
        </button>
        {error ? (
          <div className="pick-error">{forbidden ? t("pick.forbidden") : t("pick.saveError")}</div>
        ) : null}
      </td>
    </tr>
  );
}
